/**
 * The Co-Living list (Units redesign, Slice 6).
 *
 * WHAT THIS REPLACES. `CoLivingTable` — a thirteen-column table inside an
 * `overflow-x-auto` wrapper, which is why reading one unit meant scrolling
 * sideways. This renders the same units as rows that fit the viewport and
 * restack on a phone.
 *
 * THE POPULATION IS NOT RECOMPUTED HERE. The caller passes the units it already
 * holds, and this list maps them through the Slice 1 view model. That matters:
 * the page splits its segments on a raw `type` comparison that diverges from
 * `normalizeUnitTypeLabel`, so routing the population through `getUnitSegment`
 * here would silently make units visible that fall out of both segments today.
 * Which units are co-living is a question this slice deliberately does not
 * reopen.
 *
 * THE METHOD NOTE STAYS. The table carried a subtitle recording which revenue
 * basis its money columns use. Dropping it while keeping the numbers would
 * leave the figures looking more official than they are, so it moves here as a
 * quieter caption rather than disappearing with the table header.
 */
import React, { useMemo } from "react";

import { buildUnitListItems } from "./unitListModel";
import UnitListRow from "./UnitListRow";

/**
 * Where the money figures come from — carried over verbatim from the table this
 * replaces, because it describes the same numbers.
 */
export const CO_LIVING_REVENUE_NOTE =
  "Operative Kennzahlen aus Zimmerpreisen und Mietverhältnissen (Listenpreise / TenancyRevenue-Äquivalent). Auf der Unit-Detailseite: Backend-KPI-Monat.";

export default function CoLivingUnitsList({
  units,
  rooms,
  tenancies,
  resolveManagementLabel,
}) {
  const items = useMemo(
    () =>
      buildUnitListItems({
        units,
        rooms,
        tenancies,
        resolveManagementLabel,
      }),
    [units, rooms, tenancies, resolveManagementLabel]
  );

  return (
    <section>
      <p className="mb-3 text-[11px] leading-snug text-muted-foreground">
        {CO_LIVING_REVENUE_NOTE}
      </p>

      {items.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-border bg-card px-4 py-10 text-center text-[12.5px] text-muted-foreground">
          Keine Co-Living Einheiten gefunden.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <UnitListRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}
